import React, { useEffect, useRef, useState } from "react";
import { Backdrop, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import currency from "currency.js";

const FINIX_SCRIPT_SRC = "https://js.finix.com/v/2/finix.js";
const DEFAULT_FORM_ELEMENT_ID = "finix-create-order-form";
const PRODUCTION_PROJECT_ID = "bakestreet-f55a0";

const HIDDEN_FINIX_FIELDS = [
  "card_holder_name",
  "address_line1",
  "address_line2",
  "address_city",
  "address_region",
  "address_country",
];

function finixEnvironment() {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
  if (projectId === PRODUCTION_PROJECT_ID) {
    return "prod";
  }
  return "sandbox";
}

function finixAppearanceFromTheme(theme) {
  const primary = theme.palette.primary.main;
  const errorColor = theme.palette.error.main;
  const borderColor = alpha(theme.palette.text.primary, 0.23);
  const fontFamily = theme.typography.fontFamily;
  const borderRadius = `${theme.shape.borderRadius ?? 4}px`;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const paper = theme.palette.background.paper || "#fff";
  const inputHeight = "56px";

  const inputDefault = {
    color: textPrimary,
    backgroundColor: paper,
    border: `1px solid ${borderColor}`,
    borderRadius,
    boxSizing: "border-box",
    height: inputHeight,
    lineHeight: "24px",
    padding: "16.5px 14px",
    fontFamily,
    fontSize: "16px",
    fontWeight: 400,
    boxShadow: "none",
  };

  return {
    enableDarkMode: false,
    showLabels: false,
    showPlaceholders: true,
    labels: {
      address_postal_code: "Zip Code",
    },
    placeholders: {
      number: "Card Number",
      expiration_date: "MM / YY",
      security_code: "CVC",
      address_postal_code: "Zip Code",
    },
    styles: {
      default: {
        form: {
          default: {
            fontFamily,
            color: textPrimary,
            margin: 0,
            padding: 0,
            "--input-border-light": borderColor,
            "--input-bg-light": paper,
            "--option-selected-border-light": primary,
            "--error-color-light": errorColor,
            "--text-color-light": textPrimary,
            "--label-text-light": textSecondary,
          },
        },
        sectionHeader: {
          default: {
            fontFamily,
            fontSize: theme.typography.body2?.fontSize || "0.875rem",
            color: textSecondary,
            fontWeight: 400,
          },
        },
        input: {
          default: inputDefault,
          focused: {
            border: `2px solid ${primary}`,
            padding: "15.5px 13px",
            boxShadow: "none",
            outline: "none",
          },
          error: {
            border: `1px solid ${errorColor}`,
          },
        },
      },
    },
  };
}

function tokenizationErrorMessage(error) {
  if (!error) {
    return "Card could not be processed. Please check your details and try again.";
  }
  if (typeof error === "string") {
    return error;
  }
  return (
    error.message ||
    error.failure_message ||
    "Card could not be processed. Please check your details and try again."
  );
}

export default function FinixPaymentElement(props) {
  const {
    amount,
    disabled,
    validate,
    chargePaymentMethod,
    formElementId = DEFAULT_FORM_ELEMENT_ID,
  } = props;

  const theme = useTheme();
  const formRef = useRef(null);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [hasErrors, setHasErrors] = useState(true);

  useEffect(() => {
    if (typeof window.Finix !== "undefined") {
      setScriptLoaded(true);
      return;
    }

    const existing = document.querySelector(`script[src="${FINIX_SCRIPT_SRC}"]`);
    if (existing) {
      const onLoad = () => setScriptLoaded(true);
      if (existing.getAttribute("data-loaded") === "true") {
        onLoad();
        return;
      }
      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }

    const script = document.createElement("script");
    script.src = FINIX_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      script.setAttribute("data-loaded", "true");
      setScriptLoaded(true);
    };
    script.onerror = () => setError("Payment form failed to load. Please refresh the page.");
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!scriptLoaded) {
      return;
    }

    const applicationId = process.env.REACT_APP_FINIX_APPLICATION_ID;
    if (!applicationId || typeof window.Finix?.PaymentForm !== "function") {
      setError("Payment configuration error. Please refresh the page.");
      return;
    }

    const element = document.getElementById(formElementId);
    if (!element) {
      return;
    }

    let cancelled = false;
    try {
      formRef.current = window.Finix.PaymentForm(
        formElementId,
        finixEnvironment(),
        applicationId,
        {
          paymentMethods: ["card"],
          showAddress: false,
          hideFields: HIDDEN_FINIX_FIELDS,
          requiredFields: ["address_postal_code"],
          ...finixAppearanceFromTheme(theme),
          onLoad: () => {
            if (!cancelled) {
              setFormReady(true);
            }
          },
          onUpdate: (_state, _binInformation, formHasErrors) => {
            if (!cancelled) {
              setHasErrors(Boolean(formHasErrors));
            }
          },
        }
      );
    } catch (err) {
      setError("Payment configuration error. Please refresh the page.");
    }

    return () => {
      cancelled = true;
      formRef.current = null;
      element.replaceChildren();
    };
  }, [scriptLoaded, formElementId, theme]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = formRef.current;
    if (!form || processing || disabled || !formReady) {
      return;
    }

    const arr = validate();
    if (arr.length > 0) {
      setError(arr.join(". "));
      return;
    }

    setError(null);
    setProcessing(true);
    const idempotencyId = crypto.randomUUID();

    form.submit((submitError, response) => {
      if (submitError) {
        setProcessing(false);
        console.error("Finix tokenization error", submitError);
        setError(tokenizationErrorMessage(submitError));
        return;
      }

      const token = response?.data?.id;
      if (!token) {
        setProcessing(false);
        setError("Payment failed. Please try again.");
        return;
      }

      Promise.resolve(chargePaymentMethod(token, amount, idempotencyId))
        .catch((e) => {
          setError(e.response?.data?.message || e.message || "Payment failed. Please try again.");
        })
        .finally(() => {
          setProcessing(false);
        });
    });
  };

  const isButtonDisabled = processing || disabled || !formReady || hasErrors;

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <Box align="center" my={1}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      <Typography variant="overline" color="textSecondary">
        CARD DETAILS
      </Typography>
      <Paper elevation={0} sx={{ mb: 2 }}>
        <Box p={2}>
          {!scriptLoaded ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="120px">
              <CircularProgress size={24} />
              <Typography variant="caption" sx={{ ml: 1 }}>
                Loading payment form...
              </Typography>
            </Box>
          ) : (
            <>
              {!formReady && !error && (
                <Box display="flex" justifyContent="center" alignItems="center" height="80px">
                  <CircularProgress size={24} />
                </Box>
              )}
              <Box
                id={formElementId}
                sx={{
                  minHeight: formReady ? "auto" : 0,
                  lineHeight: 0,
                  "& iframe": { display: "block" },
                }}
              />
            </>
          )}
        </Box>
      </Paper>
      <Button
        sx={props.sx || ""}
        size="large"
        type="submit"
        color="primary"
        variant="contained"
        fullWidth
        disabled={isButtonDisabled || +amount < 0.5}
      >
        {currency(amount).format()} | CHARGE CARD
      </Button>
      {+amount < 0.5 && (
        <Box my={0.25} textAlign={"center"}>
          <Typography variant="caption" color="error">
            Charge amount should be $0.50 or more
          </Typography>
        </Box>
      )}
      {processing && (
        <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </form>
  );
}
